import AddAPhotoIcon from "@mui/icons-material/AddAPhoto"
import WarehouseIcon from "@mui/icons-material/Warehouse"
import { Box, TextField, Tooltip } from "@mui/material"
import {
  DataGrid,
  GridColDef,
  GridRowParams,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid"
import { StyledButton } from "components/Button/StyledButton"
import { CameraDialog } from "components/Camera/CameraDialog"
import { EUTMovementDataDialog } from "components/EUTManagementLeftBox/EUTMovementDataDialog"
import {
  DeviceEut,
  DeviceEutGroup,
  DeviceType,
  File,
  FileType,
  FileUploadInput,
  MovementData,
  User,
} from "graphql/types"
import { useStoreActions } from "hooks/storeHooks"
import { enqueueSnackbar } from "notistack"
import { FormEvent, useState } from "react"
import { emptyPerson } from "store/store"
import { getCurrentLocation } from "utils/getCurrentLocation"
import { normalizeNumberInput } from "utils/numberInputHelpers"
import { useLocalStorage } from "utils/useLocalStorage"
import { v4 } from "uuid"

type EUTGroupTabProps = {
  eut: DeviceEutGroup
  onSave: (arg: DeviceEutGroup) => void
}

export const EUTGroupTab = (props: EUTGroupTabProps) => {
  const { eut, onSave } = props
  const [user] = useLocalStorage<User>("user", emptyPerson)
  const [newEutsCount, setNewEutsCount] = useState(0)
  const [openMovementDataDialog, setOpenMovementDataDialog] = useState(false)
  const [openCameraDialog, setOpenCameraDialog] = useState<DeviceEut | null>(null)
  const [eutToChange, setEutToChange] = useState<string[]>([])

  const { uploadFile } = useStoreActions((actions) => actions)

  const columns: GridColDef[] = [
    { field: "barcode", headerName: "Barcode", width: 200 },
    {
      field: "movementData",
      headerName: "Lagerplatz",
      width: 120,
      valueGetter: (value: MovementData[]) => getCurrentLocation(value),
    },
    {
      field: "actions",
      type: "actions",
      getActions: (params: GridRowParams) => [
        <Tooltip key={"foto"} title={"Bild/Foto hinzufügen"}>
          <StyledButton
            buttonType="custom"
            customIcon={<AddAPhotoIcon fontSize="small" />}
            onClick={() => setOpenCameraDialog(params.row)}
            size="small"
            variant="contained"
          />
        </Tooltip>,

        <Tooltip key={"movement"} title={"Bewegung erfassen"}>
          <StyledButton
            buttonType="custom"
            customIcon={<WarehouseIcon fontSize="small" />}
            onClick={() => {
              setEutToChange([params.row.barcode])
              setOpenMovementDataDialog(true)
            }}
            size="small"
            variant="contained"
          />
        </Tooltip>,
      ],
    },
  ]

  const addImage = async (event: FormEvent<HTMLFormElement>, fotos: string[]) => {
    event.preventDefault()
    if (!fotos.length) {
      enqueueSnackbar("Kein Bild erfasst!", { variant: "error" })
      return
    }
    const formData = new FormData(event.currentTarget)

    const uploadImage: FileUploadInput = {
      id: "NEW",
      filename: v4() + ".png",
      name: formData.get("title") as string,
      description: formData.get("description") as string || undefined,
      base64: fotos[0],
      type: FileType.IMAGE,
      createdAt: new Date().toISOString(),
      createdBy: user.id,
    }

    const imageID = await uploadFile(uploadImage)

    const newImage: File = {
      id: imageID,
      createdAt: uploadImage.createdAt,
      createdBy: uploadImage.createdBy,
      fileURL: "",
      name: uploadImage.name,
      description: uploadImage.description,
      type: uploadImage.type,
      filename: uploadImage.filename,
    }
    if (openCameraDialog) {
      const newValue = eut.devices.map((item) => {
        if (item.id === openCameraDialog.id) return { ...item, images: [...item.images, newImage] }
        return item
      })

      const newGroup: DeviceEutGroup = {
        ...eut,
        devices: newValue,
      }
      onSave(newGroup)
    }
  }

  const handleAddEUTtoGroup = () => {
    const newEUTGroup: DeviceEutGroup = {
      ...eut,
      devices: [...eut.devices],
    }
    for (let i = 0; i < newEutsCount; i++) {
      const newDevice: DeviceEut = {
        id: v4(),
        type: DeviceType.EQUIPMENT_UNDER_TEST,
        barcode: `${eut.barcode}.${eut.devices.length + i}`,
        images: [],
        documents: [],
        note: [],
        movementData: [],
        name: eut.name,
        description: eut.description,
        manufacturer: eut.manufacturer || undefined,
        model: eut.model || undefined,
        serialNumber: eut.serialNumber ? `${eut.serialNumber}-${eut.devices.length + i}` : undefined,
        function: eut.function || undefined,
        productNumber: eut.productNumber || undefined,
        revision: eut.revision || undefined,
        software: eut.software || undefined,
        softwareVersion: eut.softwareVersion || undefined,
      }
      newEUTGroup.devices.push(newDevice)
    }
    onSave(newEUTGroup)
    setNewEutsCount(0)
  }

  const handleSaveMovementData = (value: MovementData, images: File[]) => {
    const newDevices = eut.devices.map((item) => {
      if (eutToChange.includes(item.barcode)) {
        return {
          ...item,
          images: [...item.images, ...images],
          movementData: [...item.movementData, value],
        }
      }
      return item
    })
    onSave({ ...eut, devices: newDevices })
    setEutToChange([])
  }

  const EditToolbar = () => {
    return (
      <GridToolbarContainer
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
          gap: 1,
        }}
      >
        {eutToChange.length > 0 && (
          <StyledButton
            buttonType="custom"
            customText="Logistik"
            onClick={() => setOpenMovementDataDialog(true)}
            variant="contained"
          />
        )}
        <GridToolbarQuickFilter />
      </GridToolbarContainer>
    )
  }

  return (
    <Box sx={{ p: 2 }}>
      <TextField
        value={newEutsCount || ""}
        type="number"
        onChange={(e) => {
          const value = e.currentTarget.value
          if (value === "") {
            setNewEutsCount(0) // Will show as empty due to || ""
          } else {
            const numValue = Number(value)
            setNewEutsCount(isNaN(numValue) ? 0 : Math.max(0, numValue))
          }
        }}
        onBlur={(e) => {
          const normalizedValue = normalizeNumberInput(e.currentTarget.value)
          const validValue = Math.max(0, normalizedValue) // Ensure non-negative
          if (validValue !== newEutsCount) {
            setNewEutsCount(validValue)
          }
        }}
        label="Anzahl zusätzlicher EUTs"
        size="small"
      />
      <StyledButton
        buttonType="custom"
        customText="EUT hinzufügen"
        onClick={handleAddEUTtoGroup}
        disabled={newEutsCount === 0}
        variant="contained"
      />

      <DataGrid
        sx={{ mt: 2, height: "calc(100vh - 140px)" }}
        rows={eut.devices}
        getRowId={(row) => row.id}
        columns={columns}
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        slots={{ toolbar: (props) => <EditToolbar {...props} /> }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            printOptions: { disableToolbarButton: true },
            csvOptions: { disableToolbarButton: true },
          },
        }}
        checkboxSelection
        onRowSelectionModelChange={(newRowSelectionModel) => {
          const change = eut.devices
            .filter((item) => newRowSelectionModel.includes(item.id))
            .map((item) => item.barcode)
          setEutToChange(change)
        }}
        rowSelectionModel={eut.devices
          .filter((item) => eutToChange.includes(item.barcode))
          .map((item) => item.id)}
      />

      {openCameraDialog && (
        <CameraDialog
          onSave={addImage}
          onClose={() => setOpenCameraDialog(null)}
          open={openCameraDialog !== null}
          imageName={openCameraDialog.barcode}
        />
      )}

      {/* MovementData - DIALOG */}
      <EUTMovementDataDialog
        openDialog={openMovementDataDialog}
        onClose={() => {
          setEutToChange([])
          setOpenMovementDataDialog(false)
        }}
        eut={eut}
        onSave={handleSaveMovementData}
        eutBarcode={eutToChange}
      />
    </Box>
  )
}
